import { TutorialStep } from './Tutorial';

export const getHomeTutorialSteps = (
  switchToValidationMode: () => void, 
  switchToChatMode: () => void,
  enableDemoMode: () => void,
  showDemoArticles: () => void
): TutorialStep[] => [
  {
    id: 'welcome',
    title: 'Welcome to BIM Comply!',
    content: 'Let\'s take a quick tour to help you get started with validating your building projects against regulations.',
    position: 'center',
    action: 'none',
    onEnter: () => {
      switchToValidationMode();
      enableDemoMode();
      // Don't show articles yet - wait until step 8
    },
  },
  {
    id: 'validation-container',
    title: 'Validation Results Area',
    content: 'This is where validation results will appear after you submit a BIM element for inspection. The area is currently empty, but we\'ll populate it with example results in a moment.',
    target: '.validation-mode-container',
    position: 'left',
    action: 'none',
  },
  {
    id: 'selected-element',
    title: 'Selected Element',
    content: 'This shows the type of element you selected in your BIM software. In this tutorial, we\'re inspecting a Window. Click an element in Revit, Archicad, or other BIM tools to populate this field automatically.',
    target: '.selectedElementValue',
    position: 'top',
    action: 'none',
  },
  {
    id: 'selected-country',
    title: 'Detected Country',
    content: 'This displays the country detected from your BIM model\'s location (Ireland in this example). The country determines which building regulations will be used for validation.',
    target: '.selectedCountryValue',
    position: 'top',
    action: 'none',
  },
  {
    id: 'validator',
    title: 'Choose Your Validator',
    content: 'Select the type of validation you want to perform. Different validators specialize in different aspects of building compliance.',
    target: '.dropdown',
    position: 'top',
    action: 'none',
  },
  {
    id: 'language',
    title: 'Select Your Language',
    content: 'Choose your preferred language for validation results and interface text.',
    target: '.dropdown-language',
    position: 'top',
    action: 'none',
  },
  {
    id: 'submit-button',
    title: 'Submit Validation',
    content: 'Click this button to validate your selected BIM element. The button is only enabled when you have an element selected from your BIM software. Let\'s see what happens when you submit!',
    target: '.prompt-wrapper li:last-child button',
    position: 'top',
    action: 'none',
  },
  {
    id: 'validation-results',
    title: 'Validation Results',
    content: 'Here are example validation results for a window in Dublin, Ireland! Each article shows regulatory requirements, conclusions, and compliance status. This is what appears after clicking Submit.',
    target: '.validation-mode-container',
    position: 'left',
    action: 'none',
    onEnter: () => {
      // showDemoArticles returns a Promise, but we can't await in onEnter
      // So we call it and let it run - the Next button should give it time
      showDemoArticles();
    },
  },
  {
    id: 'individual-article',
    title: 'Expand/Collapse Articles',
    content: 'Click on any article header to expand or collapse it. This helps you focus on the articles you want to review. Click Next to see the article expand!',
    target: '.validation-mode-container .article',
    position: 'right',
    action: 'none',
  },
  {
    id: 'article-title',
    title: 'Article Header',
    content: 'Each article starts with a header showing the regulation reference and title. This is the clickable area to expand/collapse the article.',
    target: '.validation-mode-container .article h2',
    position: 'bottom-screen',
    action: 'none',
    onEnter: () => {
      // Expand the article immediately
      const container = document.querySelector('.validation-mode-container');
      const firstArticle = container?.querySelector('.article') as HTMLElement;
      
      if (firstArticle && firstArticle.classList.contains('collapsed')) {
        const firstArticleH2 = firstArticle.querySelector('h2');
        if (firstArticleH2) {
          (firstArticleH2 as HTMLElement).click();
        }
      }
    },
  },
  {
    id: 'article-legislative-text',
    title: 'Legislative Text',
    content: 'The middle section contains the actual regulation text - what the building code requires for this element.',
    target: '.validation-mode-container .article .article-content blockquote',
    position: 'bottom-screen',
    action: 'none',
    onEnter: () => {
      // Scroll to show the legislative text after a delay to ensure article is expanded
      setTimeout(() => {
        const blockquote = document.querySelector('.validation-mode-container .article:first-child .article-content blockquote');
        if (blockquote) {
          blockquote.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    },
  },
  {
    id: 'article-conclusion',
    title: 'Conclusion',
    content: 'The colored conclusion shows whether your element complies. Colors indicate: green (compliant), yellow (undetermined), red (not compliant), gray (not applicable).',
    target: '.validation-mode-container .article .article-content .conclusion',
    position: 'bottom-screen',
    action: 'none',
    onExit: () => {
      // Collapse the article when leaving this step
      setTimeout(() => {
        const firstArticle = document.querySelector('.validation-mode-container .article:first-child');
        
        if (firstArticle && !firstArticle.classList.contains('collapsed')) {
          const firstArticleH2 = firstArticle.querySelector('h2');
          if (firstArticleH2) {
            (firstArticleH2 as HTMLElement).click();
          }
        }
      }, 100);
    },
  },
  {
    id: 'article-controls',
    title: 'Article Controls',
    content: 'These buttons help you manage validation results: collapse/expand all articles, scroll to top, filter by status, and search through articles. Let\'s explore each one.',
    target: '.article-controls',
    position: 'bottom',
    action: 'none',
  },
  {
    id: 'collapse-expand-all',
    title: 'Collapse/Expand All',
    content: 'This button toggles between collapsing all articles (to see just the headers) and expanding all articles (to see full content). It shows a down chevron when articles are collapsed and an up chevron when expanded. This is useful for getting an overview or diving into details.',
    target: '.article-controls .control-btn:first-child',
    position: 'bottom',
    action: 'none',
  },
  {
    id: 'scroll-to-top',
    title: 'Scroll to Top',
    content: 'This button quickly scrolls back to the top of the results. Handy when you\'ve scrolled through many articles and want to return to the beginning.',
    target: '.article-controls .control-btn:nth-child(2)',
    position: 'bottom',
    action: 'none',
  },
  {
    id: 'filter-articles',
    title: 'Filter by Status',
    content: 'Filter articles by compliance status',
    contentHTML: '<p>Click the filter button to show only articles with a specific status: <span class="status-valid">✓ Compliant</span>, <span class="status-undetermined">? Undetermined</span>, <span class="status-invalid">✗ Not Compliant</span>, or <span class="status-not-applicable">− Not Applicable</span>. This helps you focus on what needs your attention. The filter popup appears when you click this button.</p>',
    target: '.filter-btn',
    position: 'left',
    action: 'none',
  },
  {
    id: 'search-articles',
    title: 'Search Articles',
    content: 'Click the search button to open a search box where you can type keywords like "fire safety", "ventilation", or "glazing". The search will highlight matching articles and scroll to them automatically. This is useful when you have many articles and need to find specific requirements quickly.',
    target: '.search-btn',
    position: 'left',
    action: 'none',
  },
  {
    id: 'mode-switch',
    title: 'Switch to Chat Mode',
    content: 'This button at the bottom of the screen toggles between Validation Mode (for BIM elements) and Chat Mode (for general questions about regulations). Click Next to switch to Chat Mode and explore those features.',
    target: '.switch-mode',
    position: 'top',
    action: 'none',
    onExit: () => {
      // Switch to chat mode when moving to next step
      switchToChatMode();
    },
  },
  {
    id: 'chat-prompt',
    title: 'Ask Questions',
    content: 'In Chat Mode, you can ask questions about building regulations, compliance requirements, or get clarification on specific rules. Type your question in this text area.',
    target: '.promptInput',
    position: 'top',
    action: 'none',
  },
  {
    id: 'legislation',
    title: 'Select Legislation',
    content: 'In Chat Mode, you can choose which country\'s legislation to query. This dropdown appears only in Chat Mode since Validation Mode uses the country from your BIM model.',
    target: '.dropdown-legislation',
    position: 'top',
    action: 'none',
  },
  {
    id: 'feedback',
    title: 'Provide Feedback',
    content: 'We highly encourage you to provide feedback after receiving responses, especially in Validation Mode. Your feedback using the thumbs up/down buttons is the only way we use to improve our algorithms and AI. It helps us provide better, more accurate validation results for everyone.',
    target: '.feedback-container > div:last-child',
    position: 'top',
    action: 'none',
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    content: 'You now know the basics of using BIM Comply. Connect your BIM software to start validating projects, or use Chat Mode to ask questions about building regulations.',
    position: 'center',
    action: 'none',
    onEnter: switchToValidationMode, // Return to validation mode at the end
  },
];
